import { Component, Element, h, Host, Method, Prop, State, Watch } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import { BulletListSchemeDefinition, BulletListSchemeName, BulletListSize } from "./constants/bullet-list-types";
import {
  BULLET_LIST_SCHEME_CONFIG,
  BULLET_LIST_SCHEME_MAP,
  BULLET_LIST_SIZE_LARGE,
  BULLET_LIST_SIZE_NORMAL
} from "./constants/bullet-list-constants";
import { getNestedChildrenOf } from "../../utils/dom-helper";
import pfx from "../../utils/style-class-prefix-helper";
import { BulletListSchemeConfiguration } from "../../constants/colors";
import { BackgroundColor } from "../background/constants/background-types";
import { StyledComponent } from "../../decorators/styled-component";
// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot [unnamed] - Holds any number of eon-ui-bullet-list-items
 * @slot editor - Use this to inject editor controls etc. in a CMS environment like AEM
 */
@Component({
  tag: "eon-ui-bullet-list",
  styleUrl: "bullet-list.scss",
  shadow: true
})
export class BulletList implements ColorableComponent<BulletListSchemeConfiguration<BulletListSchemeDefinition>> {
  // @InitLogging() sentryTransaction;
  schemeConfig: BulletListSchemeConfiguration<BulletListSchemeDefinition> = BULLET_LIST_SCHEME_CONFIG;
  schemeMap = BULLET_LIST_SCHEME_MAP;
  @State() listItems: Element[];

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEonUiBulletListElement;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: BulletListSchemeDefinition;

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: BulletListSchemeName;

  /**
   * Specifies whether the content has the normal font-size of copy text or if it should be rendered with h5 styles.
   */
  @Prop() size: BulletListSize = BULLET_LIST_SIZE_NORMAL;

  /**
   * The icon which should be shown
   */
  @Prop() iconName: string = "bulletlist";

  /**
   * The icon should be centered with fixed width
   */
  @Prop() centerIcons: boolean = false;

  /**
   * The info icons for all list items should be aligned at the extreme end of the line
   */
  @Prop() alignInfoIcons: boolean = false;

  /**
   * Whether or not the bulletList text should have a text shadow.
   * Use this if the bulletList is placed on top of an image and you need more contrast between image and large-size text.
   */
  @Prop() textShadow = false;

  /**
   * Allows to align bullet list
   */
  @Prop() alignable: boolean = false;

  /**
   * Centered position
   */
  @Prop() centered: boolean = false;

  componentWillLoad() {
    this.applyScheme();
    this.listItems = Array.from(this.hostElement.children);
  }

  componentDidLoad() {
    this.applyAttributes();
    this.setCssVariables();
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  @Watch("size")
  @Watch("iconName")
  @Watch("centerIcons")
  @Watch("alignInfoIcons")
  applyAttributes() {
    //get slotted eon-ui-bullet-list-item
    this.listItems = Array.from(getNestedChildrenOf(this.hostElement, "eon-ui-bullet-list-item"));
    this.listItems.forEach((listItem) => {
      listItem.setAttribute("size", this.size);
      listItem.setAttribute("centered-icon", this.centerIcons.toString());
      listItem.setAttribute("align-info-icon", `${this.alignInfoIcons}`);
      if (!listItem.getAttribute("icon-name")) {
        listItem.setAttribute("icon-name", this.iconName);
      }
    });
  }

  render() {
    return (
      <Host>
        <div
          class={pfx({
            "bullet-list-wrapper": true,
            large: this.size === BULLET_LIST_SIZE_LARGE
          })}
        >
          <slot></slot>
        </div>
        <slot name="editor" />
      </Host>
    );
  }
}
